import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { manualsAPI } from "../lib/api";
import { toast } from "sonner";
import { BUILD_VERSION, getEnvironment } from "../version";
import {
  LayoutDashboard,
  FileText,
  FileCheck,
  Calendar,
  Truck,
  Users,
  DollarSign,
  Settings,
  LogOut,
  Menu,
  X,
  ClipboardList,
  UserCog,
  Percent,
  MapPin,
  FileSignature,
  FileSpreadsheet,
  Wrench,
  RefreshCw,
  BookOpen,
  Download,
  Loader2,
  BarChart,
  Info,
  UserCircle,
  Eye,
  Building2,
  Bell,
  Gauge
} from "lucide-react";
import { Button } from "../components/ui/button";
import { cn } from "../lib/utils";

const Layout = ({ children }) => {
  const { user, logout, isAdmin, isLogistica, isVendedor, canViewFinances } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [downloadingManual, setDownloadingManual] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  // Get manual ID based on user role
  const getManualForRole = (role) => {
    const roleManualMap = {
      admin: { id: "admin", filename: "Manual_ADMIN.pdf", title: "Manual de Administrador" },
      super_admin: { id: "admin", filename: "Manual_ADMIN.pdf", title: "Manual de Administrador" },
      admin_vendedor: { id: "admin_vendedor", filename: "Manual_ADMIN_VENDEDOR.pdf", title: "Manual de Admin+Vendedor" },
      vendedor: { id: "vendedor", filename: "Manual_VENDEDOR.pdf", title: "Manual de Vendedor" },
      vendedor_logistica: { id: "vendedor_logistica", filename: "Manual_VENDEDOR_LOGISTICA.pdf", title: "Manual de Vendedor+Logística" },
      logistica: { id: "operacion", filename: "Manual_OPERACION.pdf", title: "Manual de Operación" },
      operacion: { id: "operacion", filename: "Manual_OPERACION.pdf", title: "Manual de Operación" },
      mantenimiento: { id: "mantenimiento", filename: "Manual_MANTENIMIENTO.pdf", title: "Manual de Mantenimiento" }
    };
    return roleManualMap[role] || roleManualMap.vendedor;
  };

  const handleDownloadManual = async () => {
    const manual = getManualForRole(user?.role);
    setDownloadingManual(true);
    
    try {
      const response = await manualsAPI.download(manual.id);
      
      // Create blob and download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = manual.filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success(`Manual descargado: ${manual.filename}`);
    } catch (error) {
      console.error("Error descargando manual:", error);
      toast.error("Error al descargar el manual");
    } finally {
      setDownloadingManual(false);
    }
  };

  const navItems = [
    { path: "/dashboard", icon: LayoutDashboard, label: "Dashboard", roles: ["admin", "admin_vendedor", "logistica", "vendedor", "vendedor_logistica"] },
    { path: "/cotizaciones", icon: ClipboardList, label: "Cotizaciones", roles: ["admin", "admin_vendedor", "logistica", "vendedor", "vendedor_logistica"] },
    { path: "/contratos", icon: FileCheck, label: "Contratos", roles: ["admin", "admin_vendedor", "logistica", "vendedor", "vendedor_logistica"] },
    { path: "/contratos-marco", icon: FileSignature, label: "Contratos Marco", roles: ["admin", "admin_vendedor", "vendedor", "vendedor_logistica"] },
    // CRM Section - Solo admin, admin_vendedor, vendedor
    { section: "CRM", roles: ["admin", "admin_vendedor", "vendedor"] },
    { path: "/crm/clientes", icon: UserCircle, label: "Clientes", roles: ["admin", "admin_vendedor", "vendedor"] },
    // Operations Section
    { path: "/calendario", icon: Calendar, label: "Calendario", roles: ["admin", "admin_vendedor", "logistica", "vendedor", "mantenimiento", "vendedor_logistica"] },
    { path: "/logistica", icon: Gauge, label: "Logística", roles: ["admin", "admin_vendedor", "logistica", "vendedor_logistica", "vendedor"] },
    { path: "/asignacion", icon: MapPin, label: "Asignación", roles: ["admin", "admin_vendedor", "logistica", "vendedor_logistica", "vendedor"] },
    { path: "/disponibilidad", icon: Eye, label: "Disponibilidad", roles: ["admin", "admin_vendedor", "logistica", "vendedor_logistica", "vendedor"] },
    { path: "/unidades", icon: Truck, label: "Unidades", roles: ["admin", "admin_vendedor", "logistica", "mantenimiento", "vendedor_logistica"] },
    { path: "/choferes", icon: Users, label: "Choferes", roles: ["admin", "admin_vendedor", "logistica", "vendedor_logistica"] },
    { path: "/mantenimiento", icon: Wrench, label: "Mantenimiento", roles: ["admin", "admin_vendedor", "logistica", "mantenimiento", "vendedor_logistica"] },
    { path: "/finanzas", icon: DollarSign, label: "Finanzas", roles: ["admin", "admin_vendedor"] },
    { path: "/control-financiero", icon: BarChart, label: "Control por Unidad", roles: ["admin", "admin_vendedor", "logistica", "vendedor_logistica"] },
    { path: "/comisiones", icon: Percent, label: "Comisiones", roles: ["admin", "admin_vendedor", "vendedor", "vendedor_logistica"] },
    { path: "/exportar", icon: FileSpreadsheet, label: "Exportar", roles: ["admin", "admin_vendedor", "vendedor", "logistica", "vendedor_logistica"] },
    { path: "/usuarios", icon: UserCog, label: "Usuarios", roles: ["admin", "admin_vendedor"] },
    { path: "/admin/reasignaciones", icon: RefreshCw, label: "Reasignaciones", roles: ["admin", "admin_vendedor"] },
    { path: "/configuracion", icon: Settings, label: "Configuracion", roles: ["admin", "admin_vendedor"] },
    { path: "/configuracion/alertas", icon: Bell, label: "Alertas", roles: ["admin"] },
    { path: "/configuracion/vendedores", icon: UserCog, label: "Agentes de Venta", roles: ["admin", "admin_vendedor"] },
  ];

  const filteredNavItems = navItems.filter(item => item.roles.includes(user?.role));

  const roleLabels = {
    admin: "Administrador",
    admin_vendedor: "Admin+Vendedor",
    logistica: "Logística",
    vendedor: "Vendedor",
    mantenimiento: "Mantenimiento",
    vendedor_logistica: "Vendedor+Logística"
  };

  const roleColors = {
    admin: "bg-blue-100 text-blue-800",
    admin_vendedor: "bg-indigo-100 text-indigo-800",
    logistica: "bg-amber-100 text-amber-800",
    vendedor: "bg-green-100 text-green-800",
    mantenimiento: "bg-purple-100 text-purple-800",
    vendedor_logistica: "bg-teal-100 text-teal-800"
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          "fixed top-0 left-0 z-50 h-full w-64 bg-white border-r border-slate-200 transform transition-transform duration-200 ease-in-out lg:translate-x-0",
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        )}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="h-16 flex items-center justify-between px-4 border-b border-slate-100">
            <Link to="/dashboard" className="flex items-center gap-2">
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 bg-gradient-to-br from-slate-800 to-blue-600 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-lg">T</span>
                </div>
                <div className="flex flex-col">
                  <span className="text-sm font-bold text-slate-900 leading-tight">CRM TenaTour</span>
                  <span className="text-[10px] text-slate-500 leading-tight">Portal de Reservas</span>
                </div>
              </div>
            </Link>
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden p-1 hover:bg-slate-100 rounded"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* User info */}
          <div className="px-4 py-4 border-b border-slate-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-slate-200 rounded-full flex items-center justify-center">
                <span className="text-sm font-semibold text-slate-600">
                  {user?.name?.charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-900 truncate">{user?.name}</p>
                <span className={cn("inline-block text-xs px-2 py-0.5 rounded-full", roleColors[user?.role])}>
                  {roleLabels[user?.role]}
                </span>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto py-4 px-3">
            <ul className="space-y-1">
              {filteredNavItems.map((item, index) => {
                // Handle section headers
                if (item.section) {
                  return (
                    <li key={`section-${item.section}`} className="pt-4 pb-2 first:pt-0">
                      <span className="px-3 text-xs font-semibold text-slate-400 uppercase tracking-wider">
                        {item.section}
                      </span>
                    </li>
                  );
                }
                
                const Icon = item.icon;
                const isActive = location.pathname === item.path || 
                  (item.path !== "/dashboard" && location.pathname.startsWith(item.path));
                
                return (
                  <li key={item.path}>
                    <Link
                      to={item.path}
                      onClick={() => setSidebarOpen(false)}
                      className={cn(
                        "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors",
                        isActive
                          ? "bg-slate-900 text-white"
                          : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                      )}
                      data-testid={`nav-${item.path.replace(/\//g, '-').slice(1)}`}
                    >
                      <Icon className="w-5 h-5 flex-shrink-0" />
                      <span>{item.label}</span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </nav>

          {/* Manual & Logout */}
          <div className="p-4 border-t border-slate-100 space-y-2">
            {/* Download Manual Button */}
            <Button
              variant="ghost"
              className="w-full justify-start gap-3 text-slate-600 hover:text-blue-600 hover:bg-blue-50"
              onClick={handleDownloadManual}
              disabled={downloadingManual}
              data-testid="download-my-manual-btn"
            >
              {downloadingManual ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>Descargando...</span>
                </>
              ) : (
                <>
                  <BookOpen className="w-5 h-5" />
                  <span>Mi Manual</span>
                </>
              )}
            </Button>
            
            {/* Logout Button */}
            <Button
              variant="ghost"
              className="w-full justify-start gap-3 text-slate-600 hover:text-red-600 hover:bg-red-50"
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5" />
              <span>Cerrar Sesión</span>
            </Button>
            
            {/* Version Info - visible for ALL roles */}
            <div className="mt-3 pt-3 border-t border-slate-100">
              <div className="flex items-center gap-2 text-xs text-slate-400" data-testid="version-info">
                <Info className="w-3 h-3" />
                <span>{getEnvironment()} - v{BUILD_VERSION}</span>
              </div>
            </div>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Mobile header */}
        <header className="sticky top-0 z-30 h-16 bg-white border-b border-slate-200 flex items-center justify-between px-4 lg:hidden">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2 hover:bg-slate-100 rounded-lg"
            data-testid="mobile-menu-btn"
          >
            <Menu className="w-6 h-6" />
          </button>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-to-br from-slate-800 to-blue-600 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-base">T</span>
            </div>
            <span className="text-sm font-semibold text-slate-900">TenaTour</span>
          </div>
          <div className="w-10" />
        </header>

        {/* Page content */}
        <main className="min-h-[calc(100vh-4rem)] lg:min-h-screen">
          {children}
        </main>
      </div>
    </div>
  );
};

export default Layout;
